import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const impersonating = localStorage.getItem('impersonating') === 'true';
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      setIsImpersonating(impersonating);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const data = response.data;
    
    // Check if 2FA is required
    if (data.requires_2fa) {
      return {
        requires_2fa: true,
        temp_token: data.temp_token,
        message: data.message
      };
    }
    
    const { access_token, user: userData } = data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    // Clear any impersonation state on normal login
    localStorage.removeItem('impersonating');
    localStorage.removeItem('original_token');
    localStorage.removeItem('original_user');
    setUser(userData);
    setIsImpersonating(false);
    
    return userData;
  };
  
  const verify2FA = async (tempToken, code) => {
    const response = await api.post('/auth/verify-2fa', { 
      temp_token: tempToken, 
      code: code 
    });
    const { access_token, user: userData, warning } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return { user: userData, warning };
  };

  const register = async (email, password, full_name, tenant_name, plan = null) => {
    const referralCode = localStorage.getItem('nexuscms_ref') || null;
    const response = await api.post('/auth/register', {
      email,
      password,
      full_name,
      tenant_name,
      referral_code: referralCode,
      plan,
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    // Clear referral code after successful registration
    localStorage.removeItem('nexuscms_ref');
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('impersonating');
    localStorage.removeItem('original_token');
    localStorage.removeItem('original_user');
    setUser(null);
    setIsImpersonating(false);
    window.location.href = '/login';
  };

  // Start impersonating a user
  const startImpersonation = (newToken, impersonatedUser) => {
    // Save original credentials BEFORE overwriting
    const currentToken = localStorage.getItem('token');
    const currentUser = localStorage.getItem('user');
    
    localStorage.setItem('original_token', currentToken);
    localStorage.setItem('original_user', currentUser);
    localStorage.setItem('impersonating', 'true');
    
    // Set new impersonated user credentials
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(impersonatedUser));
    
    setUser(impersonatedUser);
    setIsImpersonating(true);
  };

  // Stop impersonating and return to original user
  const stopImpersonation = () => {
    const originalToken = localStorage.getItem('original_token');
    const originalUser = localStorage.getItem('original_user');
    
    if (originalToken && originalUser) {
      localStorage.setItem('token', originalToken);
      localStorage.setItem('user', originalUser);
      localStorage.removeItem('impersonating');
      localStorage.removeItem('original_token');
      localStorage.removeItem('original_user');
      
      setUser(JSON.parse(originalUser));
      setIsImpersonating(false);
      
      // Redirect to user management
      window.location.href = '/users';
    }
  };

  // Update user state (used when impersonated user data is fetched from API)
  const setToken = (newToken) => {
    localStorage.setItem('token', newToken);
  };

  const updateUser = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      register, 
      logout, 
      loading, 
      verify2FA,
      isImpersonating,
      startImpersonation,
      stopImpersonation,
      setToken,
      updateUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
